import React, { useMemo } from 'react';

import { STORE_CHECKOUT_AI_ICON_SRC } from '../../constants/storeCheckoutAi.constants';
import type { IDesignImageThumb } from '@/common/interfaces/cart.interface';

import './StoresDesignImagesList.scss';

export interface IStoresDesignImagesListProps {
  designImages: IDesignImageThumb[];
  selectedImageSrc: string;
  onSelectImage: (src: string, index: number) => void;
  previewImagesFromAi?: 'ai-flat-lay' | 'ai-model';
}

const isDesignImageType = (type: string): boolean => {
  const t = (type || '').toLowerCase();
  return t.includes('design');
};

const isFrontOrBackSlot = (image: IDesignImageThumb | undefined): boolean => {
  if (!image) {
    return false;
  }
  const t = (image.type || '').toLowerCase();
  return t === 'front' || t === 'back';
};

const StoresDesignImagesList: React.FC<IStoresDesignImagesListProps> = ({
  designImages,
  selectedImageSrc,
  onSelectImage,
  previewImagesFromAi,
}) => {
  const selectedImage = useMemo(() => {
    const fromSelectedFlag = designImages.find((img) => img.selected);
    if (fromSelectedFlag) {
      return fromSelectedFlag;
    }
    return designImages.find((img) => img.src === selectedImageSrc) ?? designImages[0] ?? null;
  }, [designImages, selectedImageSrc]);

  const showAiBadge = Boolean(previewImagesFromAi) && isFrontOrBackSlot(selectedImage);

  return (
    <div className="store-design-view" data-testid="stores-design-images-list">
      <div
        className="store-design-view__thumbnails"
        data-testid="stores-design-images-list-thumbnails"
      >
        {designImages.map((image, index) => (
          <img
            key={`${image.type}-${image.src}`}
            src={image.src}
            loading="lazy"
            alt="Design Thumbnail"
            data-index={index}
            data-src={image.src}
            onClick={() => onSelectImage(image.src, index)}
            className={[
              'store-design-view__thumbnail',
              image.selected ? 'store-design-view__thumbnail--active' : '',
              isDesignImageType(image.type) ? 'store-design-view__thumbnail--is-design-image' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid={`stores-design-images-list-thumbnail[${index}]`}
          />
        ))}
      </div>

      <div
        className="store-design-view__main-container"
        data-testid="stores-design-images-list-main-container"
      >
        <div className="store-design-view__main" data-testid="stores-design-images-list-main">
          <img
            src={selectedImage?.src || selectedImageSrc}
            alt="Selected design preview"
            loading="lazy"
            className={[
              'store-design-view__image',
              isDesignImageType(selectedImage?.type ?? '')
                ? 'store-design-view__image--is-design-image'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid="stores-design-images-list-main-image"
          />
          {showAiBadge && (
            <div
              className="store-design-images__ai-badge"
              role="img"
              aria-label="AI-generated preview images"
            >
              <img src={STORE_CHECKOUT_AI_ICON_SRC} width={18} height={18} alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StoresDesignImagesList.displayName = 'StoresDesignImagesList';

export default StoresDesignImagesList;
